import { useCallback, useMemo } from "react";

import { compareDay, isBetweenDay, isSameDay, startOfDay } from "./dateUtils";
import type { DateRangeValue } from "./types";

export interface UseDateRangeSelectionParams {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
}

export interface DateRangeSelectionApi {
  /** 날짜 클릭/Enter 시 range 상태 전이. */
  selectDate: (date: Date) => void;
  /** range 시작 끝인가. */
  isRangeStart: (date: Date) => boolean;
  /** range 종료 끝인가. */
  isRangeEnd: (date: Date) => boolean;
  /** 양 끝(시작·종료) 중 하나인가 — primary fill 대상. */
  isEndpoint: (date: Date) => boolean;
  /** 시작~종료 사이(양 끝 제외)인가 — subtle 밴드 대상. */
  isInRange: (date: Date) => boolean;
  /** 부분 선택(start만 있고 end 미정) 중이면 그 start — hover 프리뷰 밴드 기준점. 아니면 null. */
  rangePreviewAnchor: Date | null;
}

/**
 * 범위 선택 상태 머신(RFC-date-picker §Headless 엔진).
 * - 미선택 또는 이미 완성된 range에서 클릭 → 새 range의 시작(start=날짜, end=null).
 * - start만 있는 중간 상태에서 클릭 → end 확정. 시작보다 이전 날짜면 swap해 정렬 유지.
 * controlled-only — 내부 상태 없이 value/onChange만 다룬다.
 */
export function useDateRangeSelection({
  value,
  onChange,
}: UseDateRangeSelectionParams): DateRangeSelectionApi {
  const { start, end } = value;

  const selectDate = useCallback(
    (date: Date) => {
      const day = startOfDay(date);
      // 새 range 시작: 아무것도 없거나 이미 양 끝이 다 찬 경우.
      if (start == null || end != null) {
        onChange({ start: day, end: null });
        return;
      }
      // start만 있음 → end 확정(역순이면 swap).
      if (compareDay(day, start) < 0) {
        onChange({ start: day, end: start });
      } else {
        onChange({ start, end: day });
      }
    },
    [start, end, onChange],
  );

  return useMemo(
    () => ({
      selectDate,
      isRangeStart: (date: Date) => isSameDay(date, start),
      isRangeEnd: (date: Date) => isSameDay(date, end),
      isEndpoint: (date: Date) => isSameDay(date, start) || isSameDay(date, end),
      isInRange: (date: Date) => start != null && end != null && isBetweenDay(date, start, end),
      // start만 고른 중간 상태에서만 hover 프리뷰 기준점을 노출(양 끝 다 차면 실제 밴드가 그려짐).
      rangePreviewAnchor: start != null && end == null ? start : null,
    }),
    [selectDate, start, end],
  );
}
